import React, { Fragment, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { navigate } from '@reach/router';
import { useApolloClient, useMutation, useQuery } from '@apollo/react-hooks';
import * as Yup from 'yup';
import axios from 'axios';
import styles from './EditPack.module.scss';
import bookIcon from '../../../../images/book-icon.png';
import plusCircled from '../../../../images/plus-circled.svg';

import { FormErrors } from '@squirrellearningltd/go-teacher-components';

import { GET_S3_UPLOAD_QUERY } from '../../../Book/services/graphql';
import { GET_BOOK_TYPE_QUERY } from '../../services/graphql';
import { EDIT_BOOK_TYPE_MUTATION } from '../../services/graphql';

const EditPack = ({ bookTemplateCategoryId }) => {
    const [formErrors, setFormErrors] = useState([]);
    const [iconUrl, setIconUrl] = useState('');
    const [iconPreview, setIconPreview] = useState('');
    
    const schema = Yup.object().shape({
        name: Yup.string().required('Please enter a pack name'),
        description: Yup.string().required('Please enter a pack description')
    });

    const client = useApolloClient();
    const formRef = useRef(null);

    const { register, handleSubmit, errors, watch, setValue, setError, reset } = useForm({
        validationSchema: schema,
        mode: 'onBlur',
        defaultValues: {
            name: '',
            description: '',
            backgroundColor: '',
            textColor: '',
            iconUrl: ''
        }
    });

    const { data: packData, loading: packLoading, error: packError } = useQuery(GET_BOOK_TYPE_QUERY, {
        variables: { bookTemplateCategoryId: bookTemplateCategoryId },
        onCompleted: (data) => {
            const currentPack = data?.bookTemplateCategory;
            if (currentPack) {
                reset({
                    name: currentPack.name || '',
                    description: currentPack.description || '',
                    backgroundColor: currentPack.backgroundColor || '',
                    textColor: currentPack.textColor || '',
                    iconUrl: currentPack.iconUrl || ''
                });

                setIconUrl(currentPack.iconUrl || '');
                if (currentPack.iconUrl) {
                    setIconPreview(currentPack.iconUrl);
                }
            }
        },
        onError: (error) => {
            console.error('Error fetching pack data:', error);
        }
    });

    const [editPack, { loading: editingPack }] = useMutation(EDIT_BOOK_TYPE_MUTATION, {
        refetchQueries: [
            { query: GET_BOOK_TYPE_QUERY, variables: { bookTemplateCategoryId: bookTemplateCategoryId } }
        ],
        onCompleted: ({ editBookTemplateCategory }) => {
            if (editBookTemplateCategory.errors && editBookTemplateCategory.errors.length !== 0) {
                setFormErrors(editBookTemplateCategory.errors);
                return;
            } else if (editBookTemplateCategory.bookTemplateCategory.id) {
                navigate(`/packs`);
            }
        }
    });

    const onSubmit = values => {
        const { name, description, backgroundColor, textColor } = values;
        
        editPack({
            variables: {
                bookTemplateCategoryId: bookTemplateCategoryId,
                name,
                description,
                iconUrl,
                backgroundColor,
                textColor
            }
        });
    }

    const onSelectFile = (files, objectName) => {
        if (files.length) {
            const extension = files[0].name.split('.').pop().toLowerCase();
            const allowedTypes = ['jpg', 'jpeg', 'png'];
            if (!allowedTypes.includes(extension)) {
                setError('iconUrl', 'manual', 'Only .jpg, .jpeg, and .png files are allowed.');
                return;
            }

            setError('iconUrl', 'manual', '');
            const filename = `${objectName}-${new Date().getTime()}.${extension}`;
            client.query({
                query: GET_S3_UPLOAD_QUERY,
                variables: {
                    name: filename,
                    fileType: extension,
                    object: objectName
                }
            }).then(r => {
                const options = { headers: { 'Content-Type': '', 'x-amz-acl': 'public-read' } }
                axios.put(r.data.s3UploadUrlFor.uploadUrl, files[0], options).then(s3r => {
                    if (objectName === 'iconUrl') {
                        setIconUrl(r.data.s3UploadUrlFor.uploadUrl.split('?')[0]);
                        setIconPreview(r.data.s3UploadUrlFor.uploadUrl.split('?')[0]);
                        setValue('iconUrl', r.data.s3UploadUrlFor.uploadUrl.split('?')[0]);
                    }
                })
            })
        }
    }

    const titlePreview = watch('name');
    const messagePreview = watch('description');
    const showIconPreview = watch('iconUrl');
    const backgroundColorPreview = watch('backgroundColor');
    const textColorPreview = watch('textColor');

    if (packLoading) {
        return <div>Loading pack data...</div>;
    }

    if (packError) {
        return <div>Error loading pack data: {packError.message}</div>;
    }

    return (
        <Fragment>
            <div className={styles.Header}>
                <div className={styles.TitleIconContainer}>
                    <h2 className={styles.HeaderTitle}>
                        {packData?.bookTemplateCategory?.parentId !== null ? "Edit Subcategory" : "Edit Category"}
                    </h2>
                     <img
                        width='20'
                        height='20'
                        src={bookIcon}
                        alt='book-icon'
                    />
                </div>
                <div>
                        <button
                            type="submit"
                            className={styles.PublishButton}
                            onClick={() => formRef.current?.requestSubmit()}
                        >
                            {editingPack ? 'Updating...' : 'Update'}
                        </button>          
                        {/* <button              
                            className={styles.ArchiveButton}
                        >
                            Hide
                        </button>
                    <button
                        className={styles.DeleteButton}
                    >
                        Cancel
                    </button> */}
                </div>
            </div>

            <div className={`${styles.NotificationContainer} u-m-top-2`}>

                <div className={styles.NotificationForm}>
                    <h2>Icon</h2>

                    <div
                        className={`${styles.notificationWrapper} ${styles.emptyNotification}`}
                    >
                        <input
                            name='iconUrl'
                            className={`${styles.fileUpload} basic-form__text-box`}
                            placeholder='Icon'
                            onChange={(e) =>
                                onSelectFile(
                                    e.target.files,
                                    'iconUrl'
                                )
                            }
                            type='file'
                            accept='.jpg,.jpeg,.png'
                        />

                        {iconUrl ? (
                            <img
                                className={styles.ArtWorkPreview}
                                src={iconPreview}
                                alt='icon-img'
                            />
                        ) : (
                            <img width='48' src={plusCircled} alt='plus-circle-svg' />
                        )}
                    </div>
                    {errors.iconUrl && <p className='basic-form__hint'>{errors.iconUrl.message}</p>}
            
                    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                        <h2>Title</h2>
                        <div className='basic-form__group'>
                            <input
                                name='name'
                                placeholder='Title'
                                className='basic-form__text-box'
                                ref={register}
                                type='text'
                            />
                            
                        </div>
                        {errors.name && <p className='basic-form__hint'>{errors.name.message}</p>}
                        <h2>Message</h2>
                        <div className='basic-form__group'>
                            <textarea
                                name='description'
                                placeholder='Message'
                                className='basic-form__text-box'
                                ref={register}
                                type='text'
                            />
                            
                        </div>
                        {errors.description && <p className='basic-form__hint'>{errors.description.message}</p>}
                        
                        <h2>Settings</h2>
                        <div className='basic-form__group'>
                            <input
                                name='backgroundColor'
                                placeholder='Background Color'
                                className='basic-form__text-box'
                                ref={register}
                                type='text'
                            />
                            
                        </div>
                        <div className='basic-form__group'>
                            <input
                                name='textColor'
                                placeholder='Text Color'
                                className='basic-form__text-box'
                                ref={register}
                                type='text'
                            />
                            
                        </div>
                        {formErrors.length !== 0 && <FormErrors errors={formErrors} />}
                    </form>
                </div>

                <div className={styles.NotificationPreview}>
                
                    <h2>Preview</h2>

                    <div className={styles.PreviewCard} style={{backgroundColor: backgroundColorPreview, color: textColorPreview}}>
                        
                        {showIconPreview && iconPreview && (
                          <div className={styles.ImageContainer}>
                            <img src={iconPreview} alt="icon-preview" />
                        </div>
                        )}
                        <h3>{titlePreview}</h3>
                        <p className={`${styles.MessageElipsis} u-m-top-1`}>
                            {messagePreview}
                        </p>
                    </div>
                
                </div>
            </div>

        </Fragment>
    );
}

export default EditPack;